import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "react-toastify";
import { ChevronDown, Eye, EyeOff, CalendarIcon } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import Header from "@/components/Header";
import CountrySelectDialog, {
  countries,
} from "@/components/CountrySelectDialog";
import RegistrationProgressDialog from "@/components/RegistrationProgressDialog";
import { oxpayAPI } from "@/lib/oxpay";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { phoneAuthService } from "@/lib/phoneAuth";
import { countryCodeMap } from "@/constants/countryCodeMap";

export default function RegisterUser() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [countryDialogOpen, setCountryDialogOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [emailError, setEmailError] = useState("");
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [formData, setFormData] = useState({
    email: user?.email || "", // 로그인한 사용자의 이메일을 기본값으로 설정
    title: "",
    firstName: "",
    middleName: "",
    lastName: "",
    preferredName: "",
    gender: "",
    birthday: "", // Date of birth
    placeOfBirth: "", // Place of birth
    maritalStatus: "", // Marital status
    idType: "passport", // 고정값
    idNumber: "", // 여권번호
    idDateIssue: "", // ID issue date
    idDateExpiry: "", // ID expiry date
    countryCode: "",
    countryOfIssue: "", // 여권 발급 국가
    mobileNumber: "",
    password: window.location.hostname === "localhost" ? "testtest0$" : "",
    confirmPassword:
      window.location.hostname === "localhost" ? "testtest0$$" : "", // UI용, API에는 전송하지 않음
    // Business fields
    natureOfBusiness: "",
    purposeOfAccount: "",
    employerName: "",
    employmentType: "",
    sourceOfFunds: "",
    occupation: "",
    volumeOfAssets: "",
  });
  const [passwordError, setPasswordError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [progressDialogOpen, setProgressDialogOpen] = useState(false);
  const [progressStatus, setProgressStatus] = useState("creating");
  const [progressError, setProgressError] = useState("");

  // Load existing account data and auto-fill form
  useEffect(() => {
    const loadAccountData = async () => {
      if (user?.email) {
        try {
          const { data, error } = await supabase
            .from("accounts")
            .select("*")
            .eq("email", user.email)
            .single();

          if (data && !error) {
            // Auto-fill form with existing data
            setFormData((prev) => ({
              ...prev,
              email: user.email,
              firstName: data.first_name || prev.firstName,
              middleName: data.middle_name || prev.middleName,
              lastName: data.last_name || prev.lastName,
              preferredName: data.preferred_name || prev.preferredName,
              title: data.title || prev.title,
              gender: data.gender || prev.gender,
              birthday: data.birthday || prev.birthday,
              placeOfBirth: data.place_of_birth || prev.placeOfBirth,
              nationality: data.nationality || prev.nationality,
              countryOfIssue: data.id_country_of_issue || prev.countryOfIssue,
              idType: data.id_type || prev.idType,
              idNumber: data.id_number || prev.idNumber,
              idDateIssue: data.id_date_issue || prev.idDateIssue,
              idDateExpiry: data.id_date_expiry || prev.idDateExpiry,
              maritalStatus: data.marital_status || prev.maritalStatus,
              phoneNumber:
                data.phone_number?.replace(/^\+\d+\s?/, "") || prev.phoneNumber, // Remove country code
              countryCode: prev.countryCode, // Keep current country code
              walletId: data.wallet_id || prev.walletId,
              // Business fields
              natureOfBusiness:
                data.nature_of_business || prev.natureOfBusiness,
              purposeOfAccount:
                data.purpose_of_account || prev.purposeOfAccount,
              employerName: data.employer_name || prev.employerName,
              employmentType: data.employment_type || prev.employmentType,
              sourceOfFunds: data.source_of_funds || prev.sourceOfFunds,
              occupation: data.occupation || prev.occupation,
              volumeOfAssets: data.volume_of_assets || prev.volumeOfAssets,
            }));

            // Set selected country based on phone number country code
            if (data.phone_number) {
              const phoneCountryCode = data.phone_number.match(/^\+(\d+)/)?.[1];
              if (phoneCountryCode) {
                const country = countries.find(
                  (c) => c.dialCode === phoneCountryCode
                );
                if (country) {
                  setSelectedCountry(country);
                  setFormData((prev) => ({
                    ...prev,
                    countryCode: country.code,
                  }));
                }
              }
            }

            // Passport country is already set in countryOfIssue field
          } else {
            // If no account data, just set the email
            setFormData((prev) => ({ ...prev, email: user.email }));
          }
        } catch (error) {
          console.error("Error loading account data:", error);
          setFormData((prev) => ({ ...prev, email: user.email }));
        }
      }
    };

    loadAccountData();
  }, [user]);

  // Resend countdown timer
  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => {
        setResendCountdown(resendCountdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCountdown]);

  // Detect user's country from browser
  useEffect(() => {
    const detectCountry = () => {
      // Method 1: Try to use Intl.DateTimeFormat to get timezone and infer country
      try {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const locale = navigator.language || navigator.userLanguage;

        // Map common timezones and locales to country codes
        const timezoneCountryMap = {
          "America/New_York": "US",
          "America/Chicago": "US",
          "America/Los_Angeles": "US",
          "America/Toronto": "CA",
          "Europe/London": "GB",
          "Europe/Paris": "FR",
          "Europe/Berlin": "DE",
          "Europe/Madrid": "ES",
          "Asia/Tokyo": "JP",
          "Asia/Seoul": "KR",
          "Asia/Singapore": "SG",
          "Asia/Hong_Kong": "HK",
          "Asia/Shanghai": "CN",
          "Asia/Taipei": "TW",
          "Asia/Bangkok": "TH",
          "Asia/Kuala_Lumpur": "MY",
          "Asia/Jakarta": "ID",
          "Asia/Manila": "PH",
          "Asia/Ho_Chi_Minh": "VN",
          "Australia/Sydney": "AU",
          "Australia/Melbourne": "AU",
          "Pacific/Auckland": "NZ",
        };

        // First try timezone mapping
        for (const [tz, code] of Object.entries(timezoneCountryMap)) {
          if (timezone.includes(tz)) {
            const country = countries.find((c) => c.code === code);
            if (country) {
              setSelectedCountry(country);
              setFormData((prev) => ({
                ...prev,
                countryCode: code,
                countryOfIssue: code,
              }));
              return;
            }
          }
        }

        // Method 2: Use locale (e.g., en-US, ko-KR, zh-CN)
        const localeCountryCode = locale.split("-")[1]?.toUpperCase();
        if (localeCountryCode) {
          const country = countries.find((c) => c.code === localeCountryCode);
          if (country) {
            setSelectedCountry(country);
            setFormData((prev) => ({
              ...prev,
              countryCode: localeCountryCode,
              countryOfIssue: localeCountryCode,
            }));
            return;
          }
        }

        // Method 3: Language-based fallback
        const languageCountryMap = {
          en: "US", // Default English to US
          zh: "CN",
          ja: "JP",
          ko: "KR",
          fr: "FR",
          de: "DE",
          es: "ES",
          vi: "VN",
          th: "TH",
          id: "ID",
          ms: "MY",
        };

        const languageCode = locale.split("-")[0];
        const countryFromLanguage = languageCountryMap[languageCode];
        if (countryFromLanguage) {
          const country = countries.find((c) => c.code === countryFromLanguage);
          if (country) {
            setSelectedCountry(country);
            setFormData((prev) => ({
              ...prev,
              countryCode: countryFromLanguage,
              countryOfIssue: countryFromLanguage,
            }));
          }
        }
      } catch (error) {
        console.log("Could not detect country from browser:", error);
      }
    };

    detectCountry();
  }, []);

  // Format phone number based on country
  const formatPhoneNumber = (value, country) => {
    // Remove all non-digits
    const cleaned = value.replace(/\D/g, "");

    if (!country) return cleaned;

    // Apply country-specific formatting
    let formatted = cleaned;
    switch (country.code) {
      case "US":
      case "CA":
        // Format: 123 456 7890
        if (cleaned.length <= 3) formatted = cleaned;
        else if (cleaned.length <= 6)
          formatted = `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`;
        else
          formatted = `${cleaned.slice(0, 3)} ${cleaned.slice(
            3,
            6
          )} ${cleaned.slice(6, 10)}`;
        break;
      case "KR":
        // Format: 10 1234 5678
        if (cleaned.length <= 2) formatted = cleaned;
        else if (cleaned.length <= 6)
          formatted = `${cleaned.slice(0, 2)} ${cleaned.slice(2)}`;
        else
          formatted = `${cleaned.slice(0, 2)} ${cleaned.slice(
            2,
            6
          )} ${cleaned.slice(6, 10)}`;
        break;
      case "JP":
        // Format: 90 1234 5678
        if (cleaned.length <= 2) formatted = cleaned;
        else if (cleaned.length <= 6)
          formatted = `${cleaned.slice(0, 2)} ${cleaned.slice(2)}`;
        else
          formatted = `${cleaned.slice(0, 2)} ${cleaned.slice(
            2,
            6
          )} ${cleaned.slice(6, 10)}`;
        break;
      case "GB":
        // Format: 7911 123456
        if (cleaned.length <= 4) formatted = cleaned;
        else formatted = `${cleaned.slice(0, 4)} ${cleaned.slice(4, 10)}`;
        break;
      case "AU":
        // Format: 412 345 678
        if (cleaned.length <= 3) formatted = cleaned;
        else if (cleaned.length <= 6)
          formatted = `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`;
        else
          formatted = `${cleaned.slice(0, 3)} ${cleaned.slice(
            3,
            6
          )} ${cleaned.slice(6, 9)}`;
        break;
      case "KH":
        // Format: 12 345 678 (Cambodia)
        if (cleaned.length <= 2) formatted = cleaned;
        else if (cleaned.length <= 5)
          formatted = `${cleaned.slice(0, 2)} ${cleaned.slice(2)}`;
        else
          formatted = `${cleaned.slice(0, 2)} ${cleaned.slice(
            2,
            5
          )} ${cleaned.slice(5, 8)}`;
        break;
      case "PH":
        // Format: 905 123 4567 (Philippines)
        if (cleaned.length <= 3) formatted = cleaned;
        else if (cleaned.length <= 6)
          formatted = `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`;
        else
          formatted = `${cleaned.slice(0, 3)} ${cleaned.slice(
            3,
            6
          )} ${cleaned.slice(6, 10)}`;
        break;
      default:
        // Default format: groups of 3-4 digits
        if (cleaned.length <= 3) formatted = cleaned;
        else if (cleaned.length <= 7)
          formatted = `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`;
        else
          formatted = `${cleaned.slice(0, 3)} ${cleaned.slice(
            3,
            7
          )} ${cleaned.slice(7)}`;
    }

    return formatted;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // For firstName, middleName and lastName, only allow English letters and spaces, max 50 chars
    if (name === "firstName" || name === "middleName" || name === "lastName") {
      // Only allow a-z, A-Z, and spaces (matching pattern ^(?i)[a-z\s]+$)
      const englishOnly = value.replace(/[^a-zA-Z\s]/g, "");
      // Limit to 50 characters
      const limited = englishOnly.slice(0, 50);
      setFormData((prev) => ({
        ...prev,
        [name]: limited,
      }));
    } else if (name === "idNumber") {
      // For passport number, only allow alphanumeric characters
      const alphanumericOnly = value.replace(/[^a-zA-Z0-9]/g, "");
      // Limit to 20 characters (typical passport number length)
      const limited = alphanumericOnly.slice(0, 20).toUpperCase();
      setFormData((prev) => ({
        ...prev,
        [name]: limited,
      }));
    } else if (name === "email") {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
      // Validate email on blur or when user has typed something
      if (value && !validateEmail(value)) {
        setEmailError("Please enter a valid email address");
      } else {
        setEmailError("");
      }
    } else if (name === "mobileNumber") {
      // Format phone number automatically
      const formatted = formatPhoneNumber(value, selectedCountry);
      setFormData((prev) => ({
        ...prev,
        [name]: formatted,
      }));
      // Reset phone verification if number changes
      if (phoneVerified) {
        setPhoneVerified(false);
        setShowOtpInput(false);
        setOtpCode("");
      }
    } else if (name === "password") {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
      // Validate password strength
      const error = validatePassword(value);
      setPasswordError(error);
      // Check if passwords match
      if (formData.confirmPassword && value !== formData.confirmPassword) {
        setPasswordError("Passwords do not match");
      }
    } else if (name === "confirmPassword") {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
      // Check if passwords match
      if (formData.password && value !== formData.password) {
        setPasswordError("Passwords do not match");
      } else if (formData.password === value) {
        setPasswordError("");
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  // Validate email format
  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  // Validate password strength
  const validatePassword = (password) => {
    if (password.length < 8) {
      return "Password must be at least 8 characters";
    }
    if (!/[a-z]/.test(password)) {
      return "Password must contain at least one lowercase letter";
    }
    if (!/[0-9]/.test(password)) {
      return "Password must contain at least one number";
    }
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      return "Password must contain at least one special character";
    }
    return "";
  };

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setFormData((prev) => ({
      ...prev,
      countryCode: country.code,
      countryOfIssue: country.code, // 여권 발급 국가도 함께 설정
    }));
  };

  // Send OTP to phone number
  const handleSendOtp = async () => {
    if (!formData.mobileNumber || !selectedCountry) {
      toast.error("Please enter a valid phone number");
      return;
    }

    setIsSendingOtp(true);
    try {
      const fullPhoneNumber = `${
        selectedCountry.mobileCode
      }${formData.mobileNumber.replace(/\s/g, "")}`;
      const { error } = await phoneAuthService.sendOTP(fullPhoneNumber);

      if (error) {
        toast.error("Failed to send verification code. Please try again.");
      } else {
        toast.success("Verification code sent!");
        setShowOtpInput(true);
        setResendCountdown(60);
      }
    } catch (error) {
      toast.error("Failed to send verification code. Please try again.");
    } finally {
      setIsSendingOtp(false);
    }
  };

  // Verify OTP code
  const handleVerifyOtp = async () => {
    if (!otpCode || otpCode.length !== 6) {
      toast.error("Please enter a 6-digit code");
      return;
    }

    setIsVerifyingOtp(true);
    try {
      const fullPhoneNumber = `${
        selectedCountry.mobileCode
      }${formData.mobileNumber.replace(/\s/g, "")}`;
      const { error } = await phoneAuthService.verifyOTP(
        fullPhoneNumber,
        otpCode
      );

      if (error) {
        toast.error("Invalid verification code. Please try again.");
        setOtpCode("");
      } else {
        toast.success("Phone number verified successfully!");
        setPhoneVerified(true);
        setShowOtpInput(false);
      }
    } catch (error) {
      toast.error("Verification failed. Please try again.");
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields (middleName is optional)
    const requiredFields = {
      title: "Title",
      firstName: "First Name",
      lastName: "Last Name",
      preferredName: "Preferred Name",
      gender: "Gender",
      birthday: "Date of Birth",
      placeOfBirth: "Place of Birth",
      maritalStatus: "Marital Status",
      idNumber: "ID Number",
      idDateIssue: "ID Issue Date",
      idDateExpiry: "ID Expiry Date",
      countryOfIssue: "Country of Issue",
      employmentType: "Employment Type",
      occupation: "Occupation",
      employerName: "Employer Name",
      natureOfBusiness: "Nature of Business",
      purposeOfAccount: "Purpose of Account",
      sourceOfFunds: "Source of Funds",
      volumeOfAssets: "Volume of Assets",
    };

    for (const [field, label] of Object.entries(requiredFields)) {
      if (!formData[field]) {
        toast.error(`${label} is required`);
        return;
      }
    }

    if (!selectedCountry) {
      toast.error("Please select a country");
      return;
    }

    if (!formData.mobileNumber) {
      toast.error("Mobile number is required");
      return;
    }

    // Skip validation for localhost testing
    if (
      window.location.hostname !== "localhost" &&
      window.location.hostname !== "preview.fellascard.com"
    ) {
      // Validate email before submission
      if (!validateEmail(formData.email)) {
        setEmailError("Please enter a valid email address");
        return;
      }

      // Check if phone is verified
      if (!phoneVerified) {
        toast.error("Please verify your phone number first");
        return;
      }
    }

    setIsLoading(true);
    setProgressDialogOpen(true);
    setProgressStatus("creating");
    setProgressError("");

    try {
      // Check if this user already has an oxpay_user_id
      const { data: currentAccount } = await supabase
        .from("accounts")
        .select("oxpay_user_id, oxpay_status")
        .eq("email", user?.email)
        .single();

      // oxpay_user_id가 있고 active 상태인 경우 (null status는 active로 간주)
      if (
        currentAccount?.oxpay_user_id &&
        (currentAccount?.oxpay_status === "active" ||
          currentAccount?.oxpay_status === null)
      ) {
        // User already registered and active
        toast.success("You are already registered!");
        setProgressDialogOpen(false);
        navigate("/home");
        return;
      }

      // Map 2-letter country codes to 3-letter ISO codes for OxPay API
      // Map 2-letter country codes to OxPay nationality values
      const nationalityMap = {
        KR: "South Korean",
        US: "American",
        JP: "Japanese",
        CN: "Chinese",
        GB: "British",
        CA: "Canadian",
        AU: "Australian",
        FR: "French",
        DE: "German",
        ES: "Spaniard",
        IT: "Italian",
        TH: "Thai",
        VN: "Vietnamese",
        PH: "Filipino",
        MY: "Malaysian",
        SG: "Singaporean",
        ID: "Indonesian",
        IN: "Indian",
        HK: "Hong Konger",
        TW: "Taiwanese",
        NZ: "New Zealander",
        BR: "Brazilian",
        MX: "Mexican",
        RU: "Russian",
        NL: "Dutch",
        BE: "Belgian",
        CH: "Swiss",
        SE: "Swede",
        NO: "Norwegian",
        DK: "Dane",
        FI: "Finn",
        PL: "Pole",
        PT: "Portuguese",
        IE: "Irish",
        AT: "Austrian",
        GR: "Greek",
        TR: "Turk",
        IL: "Israeli",
        AE: "United Arab Emirates",
        SA: "Saudi Arabian",
        ZA: "South African",
        EG: "Egyptian",
        AR: "Argentinian",
        CL: "Chilean",
        CO: "Colombian",
        PE: "Peruvian",
        VE: "Venezuelan",
        UA: "Ukrainian",
        CZ: "Czech",
        HU: "Hungarian",
        RO: "Romanian",
        BG: "Bulgarian",
        HR: "Croatian",
        RS: "Serbian",
        SK: "Slovak",
        SI: "Slovenian",
        LT: "Lithuanian",
        LV: "Latvian",
        EE: "Estonian",
        IS: "Icelander",
        LU: "Luxembourger",
        MT: "Maltese",
        CY: "Cypriot",
        MO: "Macau Chinese",
        BD: "Bangladeshi",
        PK: "Pakistani",
        LK: "Sri Lankan",
        MM: "Burmese",
        NP: "Nepalese",
        KH: "Cambodian",
        LA: "Laotian",
        BN: "Bruneian",
        MN: "Mongolian",
        KZ: "Kazakh",
        UZ: "Uzbek",
        QA: "Qatari",
        KW: "Kuwaiti",
        BH: "Bahraini",
        OM: "Omani",
        JO: "Jordanian",
        LB: "Lebanese",
        SY: "Syrian",
        IQ: "Iraqi",
        IR: "Iranian",
        AF: "Afghan",
        PS: "Palestinian",
        YE: "Yemeni",
        LY: "Libyan",
        TN: "Tunisian",
        DZ: "Algerian",
        MA: "Moroccan",
        SD: "Sudanese",
        ET: "Ethiopian",
        KE: "Kenyan",
        UG: "Ugandan",
        TZ: "Tanzanian",
        GH: "Ghanaian",
        NG: "Nigerian",
        SN: "Senegalese",
        CM: "Cameroonian",
      };

      // Helper function to ensure date is in YYYY-MM-DD format
      const ensureDateFormat = (dateString) => {
        if (!dateString) return null;
        try {
          // If already in YYYY-MM-DD format, return as is
          if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
            return dateString;
          }
          // Otherwise parse and format
          const date = new Date(dateString);
          if (isNaN(date.getTime())) return null;
          return date.toISOString().split("T")[0];
        } catch {
          return null;
        }
      };

      // Convert formData to API format
      const postData = {
        email: formData.email,
        first_name: formData.firstName,
        last_name: formData.lastName,
        mobile: formData.mobileNumber.replace(/\s/g, ""), // Remove spaces from phone number
        mobile_country_code: selectedCountry.mobileCode.replace("+", ""), // Remove + sign for numeric code
        password: formData.password,
        middle_name: formData.middleName || null,
        preferred_name: formData.preferredName || null,
        birthday: ensureDateFormat(formData.birthday), // YYYY-MM-DD format
        id_number: formData.idNumber,
        id_type: formData.idType, // 'passport'
        id_date_expiry: ensureDateFormat(formData.idDateExpiry), // YYYY-MM-DD format
        id_date_issue: ensureDateFormat(formData.idDateIssue), // YYYY-MM-DD format

        country_of_issue:
          countryCodeMap[formData.countryOfIssue || selectedCountry.code] || "", // 3-letter ISO code
        gender: formData.gender,
        title: formData.title,

        // alt_id_type: formData.idType,
        // alt_id_number: formData.idNumber,
        // alt_id_country_of_issue:
        //   countryCodeMap[formData.countryOfIssue || selectedCountry.code] || "",

        place_of_birth: formData.placeOfBirth || null,
        nationality: nationalityMap[selectedCountry.code] || "",
        marital_status: formData.maritalStatus,
        // mothers_xxx
        nature_of_business: formData.natureOfBusiness || null,
        purpose_of_account: formData.purposeOfAccount || null,
        employer_name: formData.employerName || null,
        employment_type: formData.employmentType || null,
        source_of_funds: formData.sourceOfFunds || null,
        occupation: formData.occupation || null,
        volume_of_assets: formData.volumeOfAssets || null,
      };

      // Call OxPay API to create user
      console.log("postData", postData);
      const response = await oxpayAPI.createUser(postData);

      if (response && response.body && response.body.id) {
        setProgressStatus("updating");

        // Update accounts table with all user information
        const { error: dbError } = await supabase
          .from("accounts")
          .update({
            oxpay_user_id: response.body.id,
            oxpay_status: response.body.status.state,
            first_name: response.body.name.first,
            middle_name:
              response.body.name.middle || formData.middleName || null,
            last_name: response.body.name.last,
            preferred_name: formData.preferredName || null,
            title: formData.title || null,
            gender: formData.gender || null,
            birthday: ensureDateFormat(formData.birthday), // Save birthday in database
            place_of_birth: formData.placeOfBirth || null, // Save place of birth
            marital_status: formData.maritalStatus || null, // Save marital status
            id_type: formData.idType, // 'passport'
            id_number: formData.idNumber || null,
            id_date_issue: ensureDateFormat(formData.idDateIssue), // Save ID issue date
            id_date_expiry: ensureDateFormat(formData.idDateExpiry), // Save ID expiry date
            id_country_of_issue: formData.countryOfIssue || null,
            phone_number:
              response.body.mobile.country_code + response.body.mobile.number,
            nationality: nationalityMap[selectedCountry.code] || "",
            // Business fields
            nature_of_business: formData.natureOfBusiness || null,
            purpose_of_account: formData.purposeOfAccount || null,
            employer_name: formData.employerName || null,
            employment_type: formData.employmentType || null,
            source_of_funds: formData.sourceOfFunds || null,
            occupation: formData.occupation || null,
            volume_of_assets: formData.volumeOfAssets || null,
            // OxPay 전체 응답 데이터도 JSONB에 저장
            oxpay_user_data: response.body,
            // updated_at: new Date().toISOString(),
          })
          .eq("email", user?.email);

        if (dbError) {
          console.error("Failed to update account data:", dbError);
          // Continue anyway since user account was created
        }

        setProgressStatus("success");
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (error) {
      console.error("Failed to create user:", error);
      setProgressStatus("error");

      setProgressError(
        error.message || "Failed to create account. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header showBack={true} />

      <div className="p-4 max-w-md mx-auto">
        <Card className="overflow-hidden">
          <CardHeader className="p-3">
            <CardTitle>Register User</CardTitle>
            <CardDescription>
              Enter your information to create an account.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-3">
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="space-y-1">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="john.doe@example.com"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={
                    emailError
                      ? "border-red-500"
                      : user?.email
                      ? "bg-muted pl-2"
                      : "pl-2"
                  }
                  disabled={!!user?.email} // 로그인한 사용자는 이메일 수정 불가
                  required
                />
                {emailError && !user?.email && (
                  <p className="text-xs text-red-500">{emailError}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter password"
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                      className={
                        passwordError && formData.password
                          ? "border-red-500 pr-10"
                          : "pr-10"
                      }
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="space-y-1">
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    placeholder="Confirm password"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    required
                    className={
                      passwordError && formData.confirmPassword
                        ? "border-red-500"
                        : ""
                    }
                  />
                </div>
              </div>

              {(passwordError || formData.password) && (
                <div className="space-y-1">
                  {passwordError && (
                    <p className="text-xs text-red-500">{passwordError}</p>
                  )}
                  {formData.password &&
                    !passwordError &&
                    formData.password === formData.confirmPassword && (
                      <p className="text-xs text-green-600">
                        ✓ Password requirements met
                      </p>
                    )}
                  {!passwordError && formData.password && (
                    <div className="text-xs text-muted-foreground">
                      <p>Password requirements:</p>
                      <ul className="ml-4 mt-1 space-y-1.5">
                        <li
                          className={
                            formData.password.length >= 8
                              ? "text-green-600"
                              : ""
                          }
                        >
                          {formData.password.length >= 8 ? "✓" : "•"} At least 8
                          characters
                        </li>
                        <li
                          className={
                            /[a-z]/.test(formData.password)
                              ? "text-green-600"
                              : ""
                          }
                        >
                          {/[a-z]/.test(formData.password) ? "✓" : "•"} One
                          lowercase letter
                        </li>
                        <li
                          className={
                            /[0-9]/.test(formData.password)
                              ? "text-green-600"
                              : ""
                          }
                        >
                          {/[0-9]/.test(formData.password) ? "✓" : "•"} One
                          number
                        </li>
                        <li
                          className={
                            /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(
                              formData.password
                            )
                              ? "text-green-600"
                              : ""
                          }
                        >
                          {/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(
                            formData.password
                          )
                            ? "✓"
                            : "•"}{" "}
                          One special character
                        </li>
                      </ul>
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-1"></div>

              {/* Title and Gender */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label htmlFor="title">Title</Label>
                  <Select
                    name="title"
                    value={formData.title}
                    onValueChange={(value) =>
                      setFormData((prev) => ({ ...prev, title: value }))
                    }
                  >
                    <SelectTrigger id="title">
                      <SelectValue placeholder="Select title" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Mr">Mr</SelectItem>
                      <SelectItem value="Miss">Miss</SelectItem>
                      <SelectItem value="Dr">Dr</SelectItem>
                      <SelectItem value="Mrs">Mrs</SelectItem>
                      <SelectItem value="Madam">Madam</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1">
                  <Label htmlFor="gender">Gender</Label>
                  <Select
                    name="gender"
                    value={formData.gender}
                    onValueChange={(value) =>
                      setFormData((prev) => ({ ...prev, gender: value }))
                    }
                  >
                    <SelectTrigger id="gender">
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Birthday and Marital Status */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label htmlFor="birthday">Date of Birth</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        id="birthday"
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !formData.birthday && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.birthday || "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={
                          formData.birthday
                            ? new Date(formData.birthday)
                            : undefined
                        }
                        onSelect={(date) =>
                          setFormData((prev) => ({
                            ...prev,
                            birthday: date
                              ? `${date.getFullYear()}-${String(
                                  date.getMonth() + 1
                                ).padStart(2, "0")}-${String(
                                  date.getDate()
                                ).padStart(2, "0")}`
                              : "",
                          }))
                        }
                        disabled={(date) =>
                          date > new Date() || date < new Date("1900-01-01")
                        }
                        captionLayout="dropdown"
                        fromYear={1900}
                        toYear={new Date().getFullYear()}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-1">
                  <Label htmlFor="placeOfBirth">Place of Birth</Label>
                  <Input
                    id="placeOfBirth"
                    name="placeOfBirth"
                    placeholder="City / Country"
                    value={formData.placeOfBirth}
                    onChange={handleInputChange}
                    className="pl-2"
                  />
                </div>
              </div>

              {/* Marital Status */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label htmlFor="maritalStatus">Marital Status</Label>
                  <Select
                    name="maritalStatus"
                    value={formData.maritalStatus}
                    onValueChange={(value) =>
                      setFormData((prev) => ({ ...prev, maritalStatus: value }))
                    }
                    required
                  >
                    <SelectTrigger id="maritalStatus">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Single">Single</SelectItem>
                      <SelectItem value="Married">Married</SelectItem>
                      <SelectItem value="Divorced">Divorced</SelectItem>
                      <SelectItem value="Widowed">Widowed</SelectItem>
                      <SelectItem value="NA">NA</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div></div>
              </div>

              {/* Names */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label htmlFor="firstName">First Name</Label>
                  <Input
                    id="firstName"
                    name="firstName"
                    placeholder="John"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    pattern="^[A-Za-z\s]+$"
                    maxLength={50}
                    title="Only English letters and spaces allowed (max 50 characters)"
                    required
                    className="pl-2"
                  />
                  {formData.firstName && formData.firstName.length > 45 && (
                    <p className="text-xs text-muted-foreground">
                      {50 - formData.firstName.length} characters remaining
                    </p>
                  )}
                </div>

                <div className="space-y-1">
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    id="lastName"
                    name="lastName"
                    placeholder="Doe"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    pattern="^[A-Za-z\s]+$"
                    maxLength={50}
                    title="Only English letters and spaces allowed (max 50 characters)"
                    required
                    className="pl-2"
                  />
                  {formData.lastName && formData.lastName.length > 45 && (
                    <p className="text-xs text-muted-foreground">
                      {50 - formData.lastName.length} characters remaining
                    </p>
                  )}
                </div>
              </div>

              {/* Middle Name and Preferred Name */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label htmlFor="middleName">Middle Name (Optional)</Label>
                  <Input
                    id="middleName"
                    name="middleName"
                    placeholder="Middle name"
                    value={formData.middleName}
                    onChange={handleInputChange}
                    pattern="^[A-Za-z\s]*$"
                    maxLength={50}
                    title="Only English letters and spaces allowed (max 50 characters)"
                    className="pl-2"
                  />
                </div>

                <div className="space-y-1">
                  <Label htmlFor="preferredName">Preferred Name</Label>
                  <Input
                    id="preferredName"
                    name="preferredName"
                    placeholder="Preferred name"
                    disabled={!!formData?.walletId}
                    value={formData.preferredName}
                    onChange={handleInputChange}
                    pattern="^[A-Za-z\s]+$"
                    maxLength={25}
                    title="Only English letters and spaces allowed (max 25 characters)"
                    required
                    className="pl-2"
                  />
                </div>
              </div>

              {/* ID Type and Number */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label htmlFor="idType">ID Type</Label>
                  <Select
                    name="idType"
                    value={formData.idType}
                    onValueChange={(value) =>
                      setFormData((prev) => ({ ...prev, idType: value }))
                    }
                  >
                    <SelectTrigger id="idType">
                      <SelectValue placeholder="Select ID type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="passport">Passport</SelectItem>
                      <SelectItem value="nric">NRIC or Driver's ID</SelectItem>
                      <SelectItem value="ep">Employee Pass</SelectItem>
                      <SelectItem value="sp">Student Pass</SelectItem>
                      <SelectItem value="wp">Work Pass</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1">
                  <Label htmlFor="idNumber">ID Number</Label>
                  <Input
                    id="idNumber"
                    name="idNumber"
                    placeholder={
                      formData.idType === "passport"
                        ? "AB1234567"
                        : "Enter ID number"
                    }
                    value={formData.idNumber}
                    onChange={handleInputChange}
                    pattern="^[A-Za-z0-9]*$"
                    maxLength={20}
                    title="Only letters and numbers allowed (max 20 characters)"
                    required
                    className="pl-4 uppercase"
                  />
                </div>
              </div>
              {formData.idNumber && formData.idNumber.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  Format: Letters and numbers only
                </p>
              )}

              {/* ID Expiry and Issue Dates (swapped) */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label htmlFor="idDateIssue">ID Issue Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        id="idDateIssue"
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !formData.idDateIssue && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.idDateIssue || "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={
                          formData.idDateIssue
                            ? new Date(formData.idDateIssue)
                            : undefined
                        }
                        onSelect={(date) =>
                          setFormData((prev) => ({
                            ...prev,
                            idDateIssue: date
                              ? `${date.getFullYear()}-${String(
                                  date.getMonth() + 1
                                ).padStart(2, "0")}-${String(
                                  date.getDate()
                                ).padStart(2, "0")}`
                              : "",
                          }))
                        }
                        disabled={(date) =>
                          date > new Date() || date < new Date("1900-01-01")
                        }
                        captionLayout="dropdown"
                        fromYear={1900}
                        toYear={new Date().getFullYear()}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-1">
                  <Label htmlFor="idDateExpiry">ID Expiry Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        id="idDateExpiry"
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !formData.idDateExpiry && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.idDateExpiry || "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={
                          formData.idDateExpiry
                            ? new Date(formData.idDateExpiry)
                            : undefined
                        }
                        onSelect={(date) =>
                          setFormData((prev) => ({
                            ...prev,
                            idDateExpiry: date
                              ? `${date.getFullYear()}-${String(
                                  date.getMonth() + 1
                                ).padStart(2, "0")}-${String(
                                  date.getDate()
                                ).padStart(2, "0")}`
                              : "",
                          }))
                        }
                        disabled={(date) => date < new Date("1900-01-01")}
                        captionLayout="dropdown"
                        fromYear={new Date().getFullYear()}
                        toYear={new Date().getFullYear() + 30}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {/* Contact Information Section */}
              <div className="space-y-3 pt-4">
                <h3 className="text-sm font-semibold text-foreground">
                  Contact Information
                </h3>

                <div className="space-y-1">
                  <Label htmlFor="countryCode">Country</Label>
                  <button
                    type="button"
                    onClick={() => setCountryDialogOpen(true)}
                    className="w-full flex items-center justify-between px-3 py-2 text-left border rounded-md hover:bg-accent transition-colors"
                  >
                    <span
                      className={selectedCountry ? "" : "text-muted-foreground"}
                    >
                      {selectedCountry
                        ? `${selectedCountry.name} (${selectedCountry.code})`
                        : "Select your country"}
                    </span>
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  </button>
                </div>

                <div className="space-y-1">
                  <Label htmlFor="mobileNumber">Mobile Number</Label>
                  <div className="flex items-center gap-2">
                    {selectedCountry && (
                      <div className="flex items-center h-12 px-3 bg-muted rounded-md border">
                        <span className="text-sm font-medium">
                          {selectedCountry.mobileCode}
                        </span>
                      </div>
                    )}
                    <Input
                      id="mobileNumber"
                      name="mobileNumber"
                      type="tel"
                      placeholder={
                        selectedCountry
                          ? selectedCountry.placeholder
                          : "Select country first"
                      }
                      value={formData.mobileNumber}
                      onChange={handleInputChange}
                      disabled={!selectedCountry || phoneVerified}
                      required
                      className="flex-1 pl-4"
                    />
                    {selectedCountry &&
                      formData.mobileNumber &&
                      !phoneVerified && (
                        <Button
                          type="button"
                          variant={resendCountdown > 0 ? "outline" : "default"}
                          onClick={handleSendOtp}
                          disabled={isSendingOtp || resendCountdown > 0}
                          className="whitespace-nowrap"
                        >
                          {isSendingOtp
                            ? "Sending..."
                            : resendCountdown > 0
                            ? `Resend (${resendCountdown}s)`
                            : showOtpInput
                            ? "Resend Code"
                            : "Send Code"}
                        </Button>
                      )}
                    {phoneVerified && (
                      <div className="flex items-center px-3 text-green-600">
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        <span className="ml-1 text-sm font-medium">
                          Verified
                        </span>
                      </div>
                    )}
                  </div>
                  {!selectedCountry && (
                    <p className="text-xs text-muted-foreground">
                      Please select your country first
                    </p>
                  )}
                </div>

                {showOtpInput && !phoneVerified && (
                  <div className="space-y-1">
                    <Label htmlFor="otpCode">Verification Code</Label>
                    <div className="flex gap-2">
                      <Input
                        id="otpCode"
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        placeholder="Enter 6-digit code"
                        value={otpCode}
                        onChange={(e) =>
                          setOtpCode(e.target.value.replace(/\D/g, ""))
                        }
                        className="flex-1 pl-4"
                      />
                      <Button
                        type="button"
                        onClick={handleVerifyOtp}
                        disabled={isVerifyingOtp || otpCode.length !== 6}
                      >
                        {isVerifyingOtp ? "Verifying..." : "Verify"}
                      </Button>
                    </div>
                    {/* <p className="text-xs text-muted-foreground">
                    Enter the 6-digit code sent to your phone
                  </p> */}
                  </div>
                )}
              </div>

              {/* Business Information Section */}
              <div className="space-y-3 pt-4">
                <h3 className="text-sm font-semibold text-foreground">
                  Business Information
                </h3>

                {/* Employment Type and Occupation */}
                <div className="grid grid-cols-1 gap-4">
                  <div className="space-y-1">
                    <Label htmlFor="employmentType">Employment Type</Label>
                    <Select
                      name="employmentType"
                      value={formData.employmentType}
                      onValueChange={(value) =>
                        setFormData((prev) => ({
                          ...prev,
                          employmentType: value,
                        }))
                      }
                    >
                      <SelectTrigger id="employmentType">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="employed">Employed</SelectItem>
                        <SelectItem value="self_employed">
                          Self-employed
                        </SelectItem>
                        <SelectItem value="unemployed">Unemployed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1">
                    <Label htmlFor="occupation">Occupation</Label>
                    <Select
                      value={formData.occupation}
                      onValueChange={(value) =>
                        setFormData((prev) => ({
                          ...prev,
                          occupation: value,
                        }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select occupation" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="accountant">Accountant</SelectItem>
                        <SelectItem value="actor">Actor</SelectItem>
                        <SelectItem value="advertising_and_public_relations_manager">
                          Advertising and Public Relations Manager
                        </SelectItem>
                        <SelectItem value="army">Army</SelectItem>
                        <SelectItem value="artist_group_performance">
                          Artist (Group Performance)
                        </SelectItem>
                        <SelectItem value="athlete_and_sports_player">
                          Athlete and Sports Player
                        </SelectItem>
                        <SelectItem value="bank_teller_and_related_clerk">
                          Bank Teller and Related Clerk
                        </SelectItem>
                        <SelectItem value="barber">Barber</SelectItem>
                        <SelectItem value="bartender">Bartender</SelectItem>
                        <SelectItem value="bus_and_tram_driver">
                          Bus and Tram Driver
                        </SelectItem>
                        <SelectItem value="chef">Chef</SelectItem>
                        <SelectItem value="civil_defence_associate_professional">
                          Civil Defence Associate Professional
                        </SelectItem>
                        <SelectItem value="cleaner">Cleaner</SelectItem>
                        <SelectItem value="cleaning_and_housekeeping_supervisor_in_office">
                          Cleaning and Housekeeping Supervisor in Office
                        </SelectItem>
                        <SelectItem value="construction_supervisor">
                          Construction Supervisor
                        </SelectItem>
                        <SelectItem value="construction_worker">
                          Construction Worker
                        </SelectItem>
                        <SelectItem value="cook">Cook</SelectItem>
                        <SelectItem value="dancer_and_choreographer">
                          Dancer and Choreographer
                        </SelectItem>
                        <SelectItem value="domestic_housekeeper">
                          Domestic Housekeeper
                        </SelectItem>
                        <SelectItem value="domestic_worker">
                          Domestic Worker
                        </SelectItem>
                        <SelectItem value="driver">Driver</SelectItem>
                        <SelectItem value="early_childhood_educator">
                          Early Childhood Educator
                        </SelectItem>
                        <SelectItem value="engineering_professional_excluding_electrotechnology_not_elsewhere_classified">
                          Engineering Professional (Excluding Electrotechnology)
                          not elsewhere Classified
                        </SelectItem>
                        <SelectItem value="farm_worker">Farm Worker</SelectItem>
                        <SelectItem value="fast_food_staff">
                          Fast Food Staff
                        </SelectItem>
                        <SelectItem value="field_crop_grower">
                          Field Crop Grower
                        </SelectItem>
                        <SelectItem value="film_staff">Film Staff</SelectItem>
                        <SelectItem value="finance_manager_agriculture">
                          Finance Manager - Agriculture
                        </SelectItem>
                        <SelectItem value="finance_manager_construction">
                          Finance Manager - Construction
                        </SelectItem>
                        <SelectItem value="finance_manager_manufacturing">
                          Finance Manager - Manufacturing
                        </SelectItem>
                        <SelectItem value="finance_manager_others">
                          Finance Manager - Others
                        </SelectItem>
                        <SelectItem value="finance_manager_plantation">
                          Finance Manager - Plantation
                        </SelectItem>
                        <SelectItem value="finance_manager_services">
                          Finance Manager - Services
                        </SelectItem>
                        <SelectItem value="financial_analyst">
                          Financial Analyst
                        </SelectItem>
                        <SelectItem value="financial_and_investment_adviser">
                          Financial and Investment Adviser
                        </SelectItem>
                        <SelectItem value="fire_fighter">
                          Fire Fighter
                        </SelectItem>
                        <SelectItem value="fishery_and_aquaculture_labourer">
                          Fishery and Aquaculture Labourer
                        </SelectItem>
                        <SelectItem value="fitness_and_recreation_instructor_and_programme_leader">
                          Fitness and Recreation Instructor and Programme Leader
                        </SelectItem>
                        <SelectItem value="forestry_labourer">
                          Forestry Labourer
                        </SelectItem>
                        <SelectItem value="garden_and_horticultural_labourer">
                          Garden and Horticultural Labourer
                        </SelectItem>
                        <SelectItem value="gardener">Gardener</SelectItem>
                        <SelectItem value="hairdresser_or_salon_staff">
                          Hairdresser or Salon Staff
                        </SelectItem>
                        <SelectItem value="health_services_manager">
                          Health Services Manager
                        </SelectItem>
                        <SelectItem value="heavy_truck_and_lorry_driver">
                          Heavy Truck and Lorry Driver
                        </SelectItem>
                        <SelectItem value="hotel_or_resort_professional">
                          Hotel or Resort Professional
                        </SelectItem>
                        <SelectItem value="housewife">Housewife</SelectItem>
                        <SelectItem value="human_resource_manager_agriculture">
                          Human Resource Manager - Agriculture
                        </SelectItem>
                        <SelectItem value="human_resource_manager_construction">
                          Human Resource Manager - Construction
                        </SelectItem>
                        <SelectItem value="human_resource_manager_manufacturing">
                          Human Resource Manager - Manufacturing
                        </SelectItem>
                        <SelectItem value="human_resource_manager_plantation">
                          Human Resource Manager - Plantation
                        </SelectItem>
                        <SelectItem value="human_resource_manager_services">
                          Human Resource Manager - Services
                        </SelectItem>
                        <SelectItem value="immigration_or_customs_officer_assistant">
                          Immigration or Customs Officer/Assistant
                        </SelectItem>
                        <SelectItem value="information_and_communications_technology_manager">
                          Information and Communications Technology Manager
                        </SelectItem>
                        <SelectItem value="insurance_agent">
                          Insurance Agent
                        </SelectItem>
                        <SelectItem value="lifting_truck_operator">
                          Lifting Truck Operator
                        </SelectItem>
                        <SelectItem value="livestock_farm_labourer">
                          Livestock Farm Labourer
                        </SelectItem>
                        <SelectItem value="locomotive_engine_driver">
                          Locomotive Engine Driver
                        </SelectItem>
                        <SelectItem value="manufacturing_supervisor">
                          Manufacturing Supervisor
                        </SelectItem>
                        <SelectItem value="manufacturing_worker">
                          Manufacturing Worker
                        </SelectItem>
                        <SelectItem value="masseur">Masseur</SelectItem>
                        <SelectItem value="mixed_crop_and_livestock_farm_labourer">
                          Mixed Crop and Livestock farm Labourer
                        </SelectItem>
                        <SelectItem value="mixed_crop_grower">
                          Mixed Crop Grower
                        </SelectItem>
                        <SelectItem value="musician">Musician</SelectItem>
                        <SelectItem value="navy">Navy</SelectItem>
                        <SelectItem value="personal_care_worker_in_health_services_not_elsewhere_classified">
                          Personal Care Worker in Health Services not elsewhere
                          Classified
                        </SelectItem>
                        <SelectItem value="petroleum_and_natural_gas_refining_plant_operator">
                          Petroleum and Natural Gas Refining Plant Operator
                        </SelectItem>
                        <SelectItem value="plantation_worker">
                          Plantation Worker
                        </SelectItem>
                        <SelectItem value="police_officer">
                          Police Officer
                        </SelectItem>
                        <SelectItem value="pre_press_worker">
                          Pre-Press Worker
                        </SelectItem>
                        <SelectItem value="primary_school">
                          Primary School
                        </SelectItem>
                        <SelectItem value="print_finishing_and_binding_worker">
                          Print Finishing and Binding Worker
                        </SelectItem>
                        <SelectItem value="printer">Printer</SelectItem>
                        <SelectItem value="printing_and_photo_engravers_and_etcher">
                          Printing and Photo Engravers and Etcher
                        </SelectItem>
                        <SelectItem value="prison_guard">
                          Prison Guard
                        </SelectItem>
                        <SelectItem value="protective_services_worker_not_elsewhere_classified">
                          Protective Services Worker not elsewhere Classified
                        </SelectItem>
                        <SelectItem value="receptionist">
                          Receptionist
                        </SelectItem>
                        <SelectItem value="regulatory_government_professional_not_elsewhere_classified">
                          Regulatory Government Professional not elsewhere
                          Classified
                        </SelectItem>
                        <SelectItem value="restaurant_professional">
                          Restaurant Professional
                        </SelectItem>
                        <SelectItem value="retail_and_wholesale_trade_manager">
                          Retail and Wholesale Trade Manager
                        </SelectItem>
                        <SelectItem value="retiree">Retiree</SelectItem>
                        <SelectItem value="sales_and_marketing_manager">
                          Sales and Marketing Manager
                        </SelectItem>
                        <SelectItem value="sales_worker_not_elsewhere_classified">
                          Sales Worker not elsewhere Classified
                        </SelectItem>
                        <SelectItem value="secondary_education_teacher">
                          Secondary Education Teacher
                        </SelectItem>
                        <SelectItem value="security_guard">
                          Security Guard
                        </SelectItem>
                        <SelectItem value="security_manager">
                          Security Manager
                        </SelectItem>
                        <SelectItem value="senior_government_officials">
                          Senior Government Officials
                        </SelectItem>
                        <SelectItem value="ship_deck_crews_and_related_worker">
                          Ship Deck Crews and Related Worker
                        </SelectItem>
                        <SelectItem value="ship_deck_officer_and_pilot">
                          Ship Deck Officer and Pilot
                        </SelectItem>
                        <SelectItem value="ship_engineer">
                          Ship Engineer
                        </SelectItem>
                        <SelectItem value="shop_assistant">
                          Shop Assistant
                        </SelectItem>
                        <SelectItem value="shop_supervisor">
                          Shop Supervisor
                        </SelectItem>
                        <SelectItem value="shopkeeper">Shopkeeper</SelectItem>
                        <SelectItem value="shrub_crop_grower">
                          Shrub Crop Grower
                        </SelectItem>
                        <SelectItem value="social_welfare_manager">
                          Social Welfare Manager
                        </SelectItem>
                        <SelectItem value="social_work_associate_professional">
                          Social Work Associate Professional
                        </SelectItem>
                        <SelectItem value="sports_and_recreation_centre_manager">
                          Sports and Recreation Centre Manager
                        </SelectItem>
                        <SelectItem value="sports_coach">
                          Sports Coach
                        </SelectItem>
                        <SelectItem value="stall_and_market_salesperson">
                          Stall and Market Salesperson
                        </SelectItem>
                        <SelectItem value="street_and_related_service_worker">
                          Street and Related Service Worker
                        </SelectItem>
                        <SelectItem value="street_food_salesperson">
                          Street Food Salesperson
                        </SelectItem>
                        <SelectItem value="street_vendors_or_stall_vendor">
                          Street Vendors or Stall Vendor
                        </SelectItem>
                        <SelectItem value="student">Student</SelectItem>
                        <SelectItem value="surface_and_window_cleaner">
                          Surface and Window Cleaner
                        </SelectItem>
                        <SelectItem value="teaching_professional_not_elsewhere_classified">
                          Teaching Professional not elsewhere Classified
                        </SelectItem>
                        <SelectItem value="telecommunications_engineer">
                          Telecommunications Engineer
                        </SelectItem>
                        <SelectItem value="tourist">Tourist</SelectItem>
                        <SelectItem value="transport_conductor">
                          Transport Conductor
                        </SelectItem>
                        <SelectItem value="travel_attendant_and_travel_steward">
                          Travel Attendant and Travel Steward
                        </SelectItem>
                        <SelectItem value="travel_consultant_and_related_clerk">
                          Travel Consultant and Related Clerk
                        </SelectItem>
                        <SelectItem value="travel_guide_or_agent">
                          Travel Guide or Agent
                        </SelectItem>
                        <SelectItem value="university_and_higher_education_teaching_professional">
                          University and Higher Education Teaching Professional
                        </SelectItem>
                        <SelectItem value="vegetable_grower">
                          Vegetable Grower
                        </SelectItem>
                        <SelectItem value="waiter">Waiter</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Employer Name and Nature of Business */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label htmlFor="employerName">Employer Name</Label>
                    <Input
                      id="employerName"
                      name="employerName"
                      placeholder="Company where you work"
                      value={formData.employerName}
                      onChange={handleInputChange}
                      maxLength={50}
                      className="pl-2"
                    />
                  </div>

                  <div className="space-y-1">
                    <Label htmlFor="natureOfBusiness">Nature of Business</Label>
                    <Input
                      id="natureOfBusiness"
                      name="natureOfBusiness"
                      placeholder="e.g., Technology"
                      value={formData.natureOfBusiness}
                      onChange={handleInputChange}
                      className="pl-2"
                    />
                  </div>
                </div>

                {/* Purpose of Account and Source of Funds */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label htmlFor="purposeOfAccount">Purpose of Account</Label>
                    <Select
                      name="purposeOfAccount"
                      value={formData.purposeOfAccount}
                      onValueChange={(value) =>
                        setFormData((prev) => ({
                          ...prev,
                          purposeOfAccount: value,
                        }))
                      }
                    >
                      <SelectTrigger id="purposeOfAccount">
                        <SelectValue placeholder="Select purpose" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Personal">Personal Use</SelectItem>
                        <SelectItem value="Business">Business Use</SelectItem>
                        <SelectItem value="Savings">Savings</SelectItem>
                        <SelectItem value="Investment">Investment</SelectItem>
                        <SelectItem value="Travel">Travel</SelectItem>
                        <SelectItem value="Education">Education</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1">
                    <Label htmlFor="sourceOfFunds">Source of Funds</Label>
                    <Select
                      name="sourceOfFunds"
                      value={formData.sourceOfFunds}
                      onValueChange={(value) =>
                        setFormData((prev) => ({
                          ...prev,
                          sourceOfFunds: value,
                        }))
                      }
                    >
                      <SelectTrigger id="sourceOfFunds">
                        <SelectValue placeholder="Select source" />
                      </SelectTrigger>
                      <SelectContent>
                        {/* Personal Sources */}
                        <SelectItem value="salary">Salary/Wages</SelectItem>
                        <SelectItem value="business income">
                          Business Income
                        </SelectItem>
                        <SelectItem value="investment">
                          Investment Returns
                        </SelectItem>
                        <SelectItem value="personal savings">
                          Personal Savings
                        </SelectItem>
                        <SelectItem value="inheritance">Inheritance</SelectItem>
                        <SelectItem value="gift">
                          Gift/Family Support
                        </SelectItem>
                        {/* Business/Corporate Sources */}
                        <SelectItem value="debt">Debt</SelectItem>
                        <SelectItem value="debt capital">
                          Debt Capital
                        </SelectItem>
                        <SelectItem value="directors capital">
                          Directors Capital
                        </SelectItem>
                        <SelectItem value="equity capital">
                          Equity Capital
                        </SelectItem>
                        <SelectItem value="term loan">Term Loan</SelectItem>
                        <SelectItem value="venture funding">
                          Venture Funding
                        </SelectItem>
                        <SelectItem value="others">Others</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Volume of Assets */}
                <div className="space-y-1">
                  <Label htmlFor="volumeOfAssets">Volume of Assets</Label>
                  <Select
                    name="volumeOfAssets"
                    value={formData.volumeOfAssets}
                    onValueChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        volumeOfAssets: value,
                      }))
                    }
                  >
                    <SelectTrigger id="volumeOfAssets">
                      <SelectValue placeholder="Select range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="20000">20,000</SelectItem>
                      <SelectItem value="20000_45000">
                        20,000 - 45,000
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full mt-4"
                disabled={
                  window.location.hostname === "localhost" ||
                  window.location.hostname === "preview.fellascard.com"
                    ? isLoading // localhost에서는 로딩 중일 때만 비활성화
                    : isLoading ||
                      !formData.email ||
                      !formData.firstName ||
                      !formData.lastName ||
                      !formData.countryCode ||
                      !formData.mobileNumber ||
                      !formData.password ||
                      !formData.confirmPassword ||
                      !phoneVerified ||
                      !!emailError ||
                      !!passwordError
                }
              >
                {isLoading ? "Registering..." : "Register"}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* <div className="mt-4 p-4 bg-muted rounded-lg">
          <p className="text-sm text-muted-foreground">
            After creating your account, you'll be redirected to complete KYC
            (Know Your Customer) verification through Onfido. This is required
            to activate your card.
          </p>
          <p className="text-sm text-muted-foreground mt-2">
            <strong>You'll need:</strong> A valid government-issued ID and a
            device with a camera for selfie verification.
          </p>
        </div> */}
      </div>

      <CountrySelectDialog
        open={countryDialogOpen}
        onOpenChange={setCountryDialogOpen}
        onSelect={handleCountrySelect}
        selectedCountry={selectedCountry}
      />

      <RegistrationProgressDialog
        open={progressDialogOpen}
        onOpenChange={setProgressDialogOpen}
        status={progressStatus}
        error={progressError}
        onComplete={() => {
          setProgressStatus("creating");
          setProgressError("");
        }}
      />
    </div>
  );
}
